/**
 * Multi-layer Visualization System
 * Loads multiple FGB layers combined into single tiles from backend
 */

/**
 * Create a multi-layer tile URL for given layer IDs
 * @param {Array<string>} layerIds - Array of FGB item IDs
 * @param {number} z - Zoom level
 * @param {number} x - Tile X coordinate
 * @param {number} y - Tile Y coordinate
 * @returns {string} - Tile URL
 */
export const createMultiLayerTileUrl = (layerIds, z, x, y) => {
  const baseUrl = 'https://backend.geonetra.tech/api/v1/fgbtiles/multi';
  const idsParam = layerIds.join('%2C'); // URL-encoded comma
  return `${baseUrl}/${z}/${x}/${y}?fgb_item_ids=${idsParam}`;
};

/**
 * Create a URL template for OpenLayers that supports multiple layers
 * @param {Array<string>} layerIds - Array of FGB item IDs
 * @returns {string} - URL template with {z}/{x}/{y} placeholders
 */
export const createMultiLayerUrlTemplate = (layerIds) => {
  const baseUrl = 'https://backend.geonetra.tech/api/v1/fgbtiles/multi';
  const idsParam = layerIds.join('%2C'); // URL-encoded comma
  return `${baseUrl}/{z}/{x}/{y}?fgb_item_ids=${idsParam}`;
};

/**
 * Fetch a multi-layer tile and parse the response
 * @param {Array<string>} layerIds - Array of FGB item IDs
 * @param {number} z - Zoom level
 * @param {number} x - Tile X coordinate
 * @param {number} y - Tile Y coordinate
 * @returns {Promise<Object>} - Parsed tile data with layers
 */
export const fetchMultiLayerTile = async (layerIds, z, x, y) => {
  const url = createMultiLayerTileUrl(layerIds, z, x, y);

  try {
    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`Failed to fetch multi-layer tile: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();


    return data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get active layer IDs from layers state object
 * @param {Object} layersState - Object with layer keys and boolean values
 * @param {Object} layerMetadata - Metadata mapping layer keys to IDs
 * @returns {Array<string>} - Array of active layer IDs
 */
export const getActiveLayerIds = (layersState, layerMetadata) => {

  const activeIds = [];

  Object.entries(layersState).forEach(([layerKey, isActive]) => {

    if (isActive) {
      // The layerKey IS the layer ID
      // But let's check if we have metadata for it
      if (layerMetadata[layerKey]) {
        // The layerKey itself is the ID, use it directly
        activeIds.push(layerKey);
      } else {
      }
    }
  });

  return activeIds;
};

/**
 * Check if multi-layer mode should be used (more than one layer active)
 * @param {Object} layersState - Object with layer keys and boolean values
 * @returns {boolean} - True if multiple layers are active
 */
export const shouldUseMultiLayerMode = (layersState) => {
  const activeCount = Object.values(layersState).filter(Boolean).length;
  return activeCount > 1;
};

/**
 * Get layer metadata including bounds for multiple layers
 * @param {Array<string>} layerIds - Array of FGB item IDs
 * @returns {Promise<Object>} - Combined metadata for all layers
 */
export const fetchMultiLayerMetadata = async (layerIds) => {
  try {
    // Fetch metadata for all layers in parallel
    const metadataPromises = layerIds.map(async (id) => {
      const response = await fetch(
        `https://backend.geonetra.tech/api/v1/fgbtiles/metadata?fgb_item_id=${id}&schema=public`
      );
      return response.json();
    });

    const allMetadata = await Promise.all(metadataPromises);

    // Combine bounds from all layers
    let combinedBounds = null;

    allMetadata.forEach((metadata) => {
      if (metadata.bounds && metadata.bounds.length === 4) {
        const [minLng, minLat, maxLng, maxLat] = metadata.bounds;

        if (!combinedBounds) {
          combinedBounds = [minLng, minLat, maxLng, maxLat];
        } else {
          // Expand bounds to include this layer
          combinedBounds[0] = Math.min(combinedBounds[0], minLng);
          combinedBounds[1] = Math.min(combinedBounds[1], minLat);
          combinedBounds[2] = Math.max(combinedBounds[2], maxLng);
          combinedBounds[3] = Math.max(combinedBounds[3], maxLat);
        }
      }
    });

    return {
      layers: allMetadata,
      combinedBounds,
      minzoom: Math.min(...allMetadata.map(m => m.minzoom || 0)),
      maxzoom: Math.max(...allMetadata.map(m => m.maxzoom || 22)),
    };
  } catch (error) {
    throw error;
  }
};

export default {
  createMultiLayerTileUrl,
  createMultiLayerUrlTemplate,
  fetchMultiLayerTile,
  getActiveLayerIds,
  shouldUseMultiLayerMode,
  fetchMultiLayerMetadata,
};
